/** HTML de impressão de atestado — mesmo contrato que `Certificates.tsx` (GET .../print). */
import { buildClinicPrintDocumentHtml, escapeHtml } from './clinicPrintLetterhead';
import { formatTaxIdDisplay, taxIdColonLabel } from './taxIdRegional';
import {
  buildCertificateTemplateContext,
  enrichCertificateForTemplate,
  resolveCertificateTemplateContent,
} from './certificateTemplateUtils';

const typeLabels: Record<string, string> = {
  medical_leave: 'Atestado Médico',
  attendance: 'Declaração de Comparecimento',
  fit_to_work: 'Aptidão ao Trabalho',
  accompaniment: 'Acompanhante',
  custom: 'Personalizado',
};

function fmtDate(d: string | undefined | null): string {
  if (!d) return '';
  try {
    return new Date(String(d).split('T')[0] + 'T12:00:00').toLocaleDateString('pt-BR');
  } catch {
    return String(d);
  }
}

function nl2br(s: unknown): string {
  return escapeHtml(String(s ?? '')).replace(/\n/g, '<br/>');
}

/** GET .../print recusado porque a unidade exige a autorização do CID assinada. */
export function isCidConsentRequiredError(error: any): boolean {
  return error?.response?.data?.code === 'CID_CONSENT_REQUIRED';
}

/**
 * Ocupa o lugar do atestado bloqueado na impressão da evolução. Cair no texto
 * de pré-visualização imprimiria o CID justamente quando ele não pode sair.
 */
export function buildCidConsentBlockedNoticeHtml(): string {
  return `<div class="attachment-block attachment-block--embedded" style="border:1px dashed #f59e0b;border-radius:8px;padding:14px 16px;color:#92400e;font-size:12px;line-height:1.5;">
  <strong>Atestado não impresso.</strong> Ele traz o CID e o paciente ainda não assinou a autorização.
  Colete a autorização em Atestados ou retire o CID para imprimir.
</div>`;
}

export function buildCertificatePrintHtml(printData: any): string {
  const cert = enrichCertificateForTemplate(printData?.certificate || {});
  const clinic = printData?.clinic || {};
  const patient = cert?.patient || cert?.patient_snapshot || {};
  const prof = cert?.professional || cert?.professional_snapshot || {};
  const esc = escapeHtml;

  const infoRows = [
    `<div><strong>Paciente:</strong> ${esc(patient?.full_name || 'N/A')}</div>`,
    patient?.cpf ? `<div><strong>${taxIdColonLabel()}</strong> ${esc(formatTaxIdDisplay(patient.cpf))}</div>` : '',
    `<div><strong>Data:</strong> ${fmtDate(cert?.issue_date)}</div>`,
    cert?.days_off || cert?.leave_days
      ? `<div><strong>Período de afastamento:</strong> ${esc(String(cert.days_off || cert.leave_days))} dia(s)</div>`
      : '',
    cert?.start_date || cert?.leave_start_date
      ? `<div><strong>De:</strong> ${fmtDate(cert.start_date || cert.leave_start_date)} <strong>Até:</strong> ${fmtDate(cert.end_date || cert.leave_end_date)}</div>`
      : '',
    cert?.cid_code || cert?.diagnosis_code
      ? `<div><strong>CID:</strong> ${esc(cert.cid_code || cert.diagnosis_code)}${cert.cid_description || cert.diagnosis ? ' - ' + esc(cert.cid_description || cert.diagnosis) : ''}</div>`
      : '',
    cert?.companion_name
      ? `<div><strong>Acompanhante:</strong> ${esc(cert.companion_name)}${cert?.companion_cpf ? ` — ${taxIdColonLabel()} ${esc(formatTaxIdDisplay(cert.companion_cpf))}` : ''}</div>`
      : '',
  ]
    .filter(Boolean)
    .join('\n');

  // Res. CFM 1.658/2002, art. 5º, parágrafo único: a concordância com o CID
  // precisa estar expressa no próprio atestado.
  const consent = printData?.cid_consent || cert?.cid_consent;
  const hasCidLine = Boolean(cert?.cid_code || cert?.diagnosis_code);
  const consentNote = hasCidLine && consent?.status === 'signed'
    ? `<p style="font-size:10px;color:#475569;margin:4px 0 0;line-height:1.45;">CID informado a pedido e com autorização expressa do(a) paciente, assinada eletronicamente${
      consent.signer_name ? ` por ${esc(consent.signer_name)}` : ''
    }${consent.signed_at ? ` em ${esc(new Date(consent.signed_at).toLocaleDateString('pt-BR'))}` : ''}.${
      consent.validation_url
        ? ` Validação: ${esc(consent.validation_url)}${consent.validation_code ? ` (código ${esc(consent.validation_code)})` : ''}.`
        : ''
    }</p>`
    : '';

  const reg = `${esc(prof?.registration_type || 'CRM')} ${esc(prof?.registration_number || '')}${prof?.registration_state ? ` - ${esc(prof.registration_state)}` : ''}`;
  const sigImg = prof?.signature_image_url
    ? `<div class="clinic-doc-sign-prof-img-wrap"><img src="${esc(prof.signature_image_url)}" alt="" /></div>`
    : '';
  const spacerCls = sigImg ? 'clinic-doc-sign-spacer clinic-doc-sign-spacer--with-img' : 'clinic-doc-sign-spacer';
  const credentialsHtml = Array.isArray(prof?.professional_credentials)
    ? prof.professional_credentials.filter(Boolean).map((c: string) => `<p class="clinic-doc-sign-caption" style="font-style:italic;color:#374151;">${esc(c)}</p>`).join('')
    : '';

  const resolvedContent = resolveCertificateTemplateContent(
    cert?.content || '',
    buildCertificateTemplateContext(cert, patient, prof),
  );

  const bodyHtml = `
    <div class="patient-info">${infoRows}${consentNote}</div>
    <div class="attachment-body" style="line-height:2;text-align:justify;margin:18px 0;">${nl2br(resolvedContent)}</div>
    <div class="clinic-doc-sign-zone clinic-doc-sign-zone--compact">
      <div class="clinic-doc-sign-row">
        <div class="clinic-doc-sign-slot">
          ${sigImg}
          <div class="${spacerCls}"></div>
          <div class="clinic-doc-sign-rule"></div>
          <p class="clinic-doc-sign-caption">${esc(prof?.full_name || '')}</p>
          ${credentialsHtml}
          ${prof?.specialty ? `<p class="clinic-doc-sign-caption">${esc(prof.specialty)}</p>` : ''}
          <p class="clinic-doc-sign-caption">${reg}</p>
        </div>
        <div class="clinic-doc-sign-slot clinic-doc-sign-slot--narrow">
          <div class="clinic-doc-sign-spacer"></div>
          <div class="clinic-doc-sign-rule"></div>
          <p class="clinic-doc-sign-caption">Data: ${fmtDate(cert?.issue_date) || '___/___/______'}</p>
        </div>
      </div>
    </div>`;

  return buildClinicPrintDocumentHtml({
    clinic,
    browserTitle: 'Atestado',
    badgeText: typeLabels[cert?.certificate_type] || 'Atestado',
    bodyHtml,
  });
}
